import { Form, Head } from "@inertiajs/react"
import { useState } from "react"

import Heading from "@/components/heading"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import AppLayout from "@/layouts/app-layout"
import {
  newServerAppPath,
  serverAppsPath,
  serverPath,
  serversPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type { Server } from "@/types"

interface AppsNewProps {
  server: Pick<Server, "id" | "name">
}

export default function AppsNew({ server }: AppsNewProps) {
  const [appType, setAppType] = useState<"static" | "rails">("rails")
  const [appName, setAppName] = useState("")
  const [locationManuallySet, setLocationManuallySet] = useState(false)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Servers", href: serversPath() },
    { title: server.name, href: serverPath(server.id) },
    { title: "Apps", href: serverAppsPath(server.id) },
    { title: "New App", href: newServerAppPath(server.id) },
  ]

  const toSnakeCase = (str: string) =>
    str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")

  const getDefaultLocation = (name: string, type: "static" | "rails") => {
    if (!name) return ""
    const snakeCaseName = toSnakeCase(name)
    return type === "static"
      ? `/home/deploy/static/${snakeCaseName}`
      : `/home/deploy/rails/${snakeCaseName}`
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value
    setAppName(newName)
    
    if (!locationManuallySet) {
      const locationInput = document.getElementById(
        "location",
      ) as HTMLInputElement
      if (locationInput) {
        locationInput.value = getDefaultLocation(newName, appType)
      }
    }
  }

  const handleAppTypeChange = (value: string) => {
    if (!value) return
    const newType = value as "static" | "rails"
    setAppType(newType)
    
    if (!locationManuallySet && appName) {
      const locationInput = document.getElementById(
        "location",
      ) as HTMLInputElement
      if (locationInput) {
        locationInput.value = getDefaultLocation(appName, newType)
      }
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New App" />

      <div className="p-6">
        <Heading
          title="Add Application"
          description={`Create a new app on ${server.name}`}
        />

        <Card className="mt-6 max-w-2xl">
          <CardHeader>
            <CardTitle>Application Details</CardTitle>
            <CardDescription>
              Enter the basic information about your application
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form
              method="post"
              action={serverAppsPath(server.id)}
              className="space-y-6"
            >
              {({ processing, errors }) => (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="app_type">Application Type *</Label>
                    <input type="hidden" name="app[app_type]" value={appType} />
                    <ToggleGroup
                      type="single"
                      value={appType}
                      onValueChange={handleAppTypeChange}
                      variant="outline"
                      className="w-full justify-start"
                    >
                      <ToggleGroupItem value="rails" className="flex-1">
                        Rails
                      </ToggleGroupItem>
                      <ToggleGroupItem value="static" className="flex-1">
                        Static
                      </ToggleGroupItem>
                    </ToggleGroup>
                    <p className="text-muted-foreground text-xs">
                      {appType === "rails"
                        ? "Ruby on Rails application with database and environment settings"
                        : "Static site (NextJS, HTML, etc.) without database requirements"}
                    </p>
                    <InputError messages={errors["app.app_type"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="app[name]"
                      type="text"
                      required
                      autoFocus
                      placeholder="My Application"
                      onChange={handleNameChange}
                    />
                    <InputError messages={errors["app.name"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      name="app[location]"
                      type="text"
                      placeholder={getDefaultLocation("app_name", appType)}
                      defaultValue={getDefaultLocation(appName, appType)}
                      onChange={() => setLocationManuallySet(true)}
                    />
                    <p className="text-muted-foreground text-xs">
                      The local path to the application on the server
                    </p>
                    <InputError messages={errors["app.location"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="app_url">Primary URL</Label>
                    <Input
                      id="app_url"
                      name="app[app_url]"
                      type="url"
                      placeholder="https://myapp.example.com"
                    />
                    <InputError messages={errors["app.app_url"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="other_urls">Other URLs</Label>
                    <Input
                      id="other_urls"
                      name="app[other_urls]"
                      type="text"
                      placeholder="https://staging.example.com, https://api.example.com"
                    />
                    <p className="text-muted-foreground text-xs">
                      Comma-separated list of additional URLs
                    </p>
                    <InputError messages={errors["app.other_urls"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="health_urls">Health Check URLs</Label>
                    <Input
                      id="health_urls"
                      name="app[health_urls]"
                      type="text"
                      placeholder="https://myapp.example.com/health"
                    />
                    <p className="text-muted-foreground text-xs">
                      Comma-separated URLs to monitor for health
                    </p>
                    <InputError messages={errors["app.health_urls"]} />
                  </div>

                  {appType === "rails" && (
                    <>
                      <div className="grid gap-2">
                        <Label htmlFor="environment">Environment</Label>
                        <input
                          id="environment"
                          name="app[environment]"
                          type="text"
                          list="environment-options"
                          placeholder="development, staging, production..."
                          className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                        />
                        <datalist id="environment-options">
                          <option value="development" />
                          <option value="staging" />
                          <option value="production" />
                        </datalist>
                        <p className="text-muted-foreground text-xs">
                          The deployment environment for this application
                        </p>
                        <InputError messages={errors["app.environment"]} />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                          <Label htmlFor="db_type">Database Type</Label>
                          <select
                            id="db_type"
                            name="app[db_type]"
                            className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                          >
                            <option value="">None</option>
                            <option value="postgresql">PostgreSQL</option>
                            <option value="mysql">MySQL</option>
                            <option value="sqlite">SQLite</option>
                          </select>
                          <InputError messages={errors["app.db_type"]} />
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="db_name">Database Name</Label>
                          <Input
                            id="db_name"
                            name="app[db_name]"
                            type="text"
                            placeholder="myapp_production"
                          />
                          <InputError messages={errors["app.db_name"]} />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="grid gap-2">
                    <Label htmlFor="notes">Notes (Markdown)</Label>
                    <textarea
                      id="notes"
                      name="app[notes]"
                      rows={5}
                      className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[80px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Application notes and documentation..."
                    />
                    <InputError messages={errors["app.notes"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="notification_recipients">
                      Notification Recipients
                    </Label>
                    <Input
                      id="notification_recipients"
                      name="app[notification_recipients]"
                      type="text"
                      placeholder="admin@you.digital, ops@you.digital"
                    />
                    <p className="text-muted-foreground text-xs">
                      Comma-separated email addresses for backup and SSL alerts.
                      Leave empty to notify all users.
                    </p>
                    <InputError
                      messages={errors["app.notification_recipients"]}
                    />
                  </div>

                  <div className="flex justify-end gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => history.back()}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={processing}>
                      {processing && <Spinner />}
                      Create App
                    </Button>
                  </div>
                </>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
